import { useMemo } from 'react';
import { useAppSelector } from 'hooks/redux';
import { useIndexedImages } from 'hooks/useIndexedImages';
import { IProduct, IProductGroup } from 'modules/ScrShopNew/store/slices/products/types';

type OptionalProduct = IProduct | undefined;

interface UsePackageMatchingProps {
  productGroup: IProductGroup;
  selectedPackage?: IProduct | null;
  currentImagesAmount?: number;
}

interface UsePackageMatchingReturn {
  matchedPackage?: IProduct;
  alreadyPurchasedPackage?: IProduct;
  packagesSortedByTier: IProduct[];
  packagesSortedByTierWithInitialPrice: IProduct[];
  allImagesPackage?: IProduct;
  allImagesMatch: boolean;
  matchingPackage?: IProduct;
  matchingPackageExtraImages?: IProduct;
}

export const usePackageMatching = ({
  productGroup,
  selectedPackage,
  currentImagesAmount = 0
}: UsePackageMatchingProps): UsePackageMatchingReturn => {
  const collection = useAppSelector((state) => state.collection);
  // const likedImagesAmount = useAppSelector(selectCurrentLikedImages).length;
  // const selectedImagesAmount = useAppSelector(getSelectedImagesIDsSelector).length;
  const { filteredImagesArr: images } = useIndexedImages({});

  const selectedPackageImagesIncludedRaw = selectedPackage?.imagesIncluded || 0;

  // For allImagesPackages we need to ignore the imagesIncluded!
  const selectedPackageImagesIncluded = selectedPackage?.allImagesIncluded
    ? 0
    : selectedPackageImagesIncludedRaw;

  const upsellingPackages = productGroup?.products || [];

  return useMemo(() => {
    // Early return for non-digital products or empty product groups
    if (!upsellingPackages.length) {
      return {
        matchedPackage: undefined,
        alreadyPurchasedPackage: undefined,
        packagesSortedByTier: [],
        packagesSortedByTierWithInitialPrice: [],
        allImagesPackage: undefined,
        allImagesMatch: false,
        matchingPackage: undefined,
        matchingPackageExtraImages: undefined
      };
    }
    const alreadyPurchasedPackage = upsellingPackages.find(
      ({ _pricingId }) => _pricingId === collection.digitalShop.alreadyPurchasedTier
    );

    const adjustPackagePrice = (packageItem: IProduct): IProduct => {
      if (!alreadyPurchasedPackage) return packageItem;

      return {
        ...packageItem,
        priceTotal: Math.max(packageItem.priceTotal - alreadyPurchasedPackage.priceTotal, 0)
      };
    };

    const allPackages = [...upsellingPackages];
    const commonPackages = allPackages.filter((item) => !item.allImagesIncluded);

    const filteredPackages = commonPackages.filter(
      (pckg) =>
        pckg.imagesIncluded >=
        (alreadyPurchasedPackage ? alreadyPurchasedPackage.imagesIncluded : 0)
    );

    let packagesSortedByTierWithInitialPrice: IProduct[];

    const hasTiers = filteredPackages.some(
      (pckg) => typeof pckg.tier === 'number' && pckg.tier >= 0
    );

    if (hasTiers) {
      packagesSortedByTierWithInitialPrice = filteredPackages.sort(
        (a, b) => (a.tier || 0) - (b.tier || 0)
      );
    } else {
      packagesSortedByTierWithInitialPrice = filteredPackages.sort(
        (a, b) => (a.imagesIncluded || 0) - (b.imagesIncluded || 0)
      );
    }

    const packagesSortedByTier = packagesSortedByTierWithInitialPrice.map(adjustPackagePrice);

    const packagesReversed = [...packagesSortedByTier].reverse();

    const matchingPackage = packagesReversed.find(
      (pckg) => pckg.imagesIncluded === currentImagesAmount
    );
    const matchingPackageExtraImages = packagesReversed
      .filter((pckg) => currentImagesAmount > pckg.imagesIncluded)
      .reduce((max, pckg) => {
        if (!max || pckg.imagesIncluded > max.imagesIncluded) return pckg;

        return max;
      }, undefined as OptionalProduct);

    const packagesSortedByImagesIncluded = [...packagesSortedByTier].sort(
      (a, b) => a.imagesIncluded - b.imagesIncluded
    );

    const nextPackage = packagesSortedByImagesIncluded.find(
      (p) => p.imagesIncluded > selectedPackageImagesIncluded
    );

    const nextPackageImagesIncluded = nextPackage?.imagesIncluded ?? Infinity;

    const allImagesReversed = [...allPackages].reverse();
    const allImagesPackageRaw = allImagesReversed.find((pckg) => pckg.allImagesIncluded);

    const allImagesPackage = allImagesPackageRaw
      ? adjustPackagePrice(allImagesPackageRaw)
      : undefined;

    const allImagesMatch = Boolean(currentImagesAmount === images?.length && allImagesPackage);

    const currentMatchingPackage = matchingPackage || matchingPackageExtraImages;

    const equalOrMoreImagesThanSelectedPackage =
      currentImagesAmount >= selectedPackageImagesIncluded;
    const notEnoughForNextPackage = nextPackageImagesIncluded > currentImagesAmount;
    // An "all images" package is only ever matched through allImagesMatch. Its
    // imagesIncluded is ignored (treated as 0 above), so keeping it here would
    // skip the lowest tier whenever that tier also includes 0 images: the
    // "next" package would be the second tier and a partial selection would
    // stay on the "all images" package at its full price (SBK-9516).
    const shouldUseSelectedPackage =
      selectedPackage &&
      !selectedPackage.allImagesIncluded &&
      equalOrMoreImagesThanSelectedPackage &&
      notEnoughForNextPackage;

    let matchedPackage: OptionalProduct;

    if (allImagesMatch) {
      matchedPackage = allImagesPackage;
    } else if (shouldUseSelectedPackage) {
      matchedPackage = selectedPackage;
    } else {
      matchedPackage = currentMatchingPackage;
    }

    return {
      matchedPackage,
      alreadyPurchasedPackage,
      packagesSortedByTier,
      packagesSortedByTierWithInitialPrice,
      allImagesPackage,
      allImagesMatch,
      matchingPackage,
      matchingPackageExtraImages
    };
  }, [
    upsellingPackages,
    collection.digitalShop.alreadyPurchasedTier,
    currentImagesAmount,
    images?.length,
    selectedPackage
  ]);
};
